import {z} from 'zod';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {MAX_SITE_BRANCH_DESCRIPTION_REQUEST_LENGTH} from '@packages/domains/change-coordination/branches/data-types';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'create-site-branch' as const;

const argumentZod = z.object({
  name: z
    .string()
    .min(1)
    .describe('User-facing name for the new site branch (Releases).'),
  description: z
    .string()
    .max(MAX_SITE_BRANCH_DESCRIPTION_REQUEST_LENGTH)
    .optional()
    .describe('Optional description for the site branch.'),
  originPageId: z
    .string()
    .min(1)
    .optional()
    .describe(
      'Optional main-site page id (from list_pages) the user was viewing; used to choose the destination working page after create. Omit to use the site index page.'
    ),
});

export const createSiteBranchManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Create site branch',
  family: 'site-branches',
  llmTool: {
    name: toolName,
    description:
      'Creates a site-scope branch (Releases) for the current site. Sync — returns the new site-branch id and destinationPageId for create-and-enter. Does not create a page-scope branch.',
    inputSchema: argumentZod,
  },
  type: 'server',
  featureGate: FEATURE_FLAGS.SITE_BRANCHES,
  requiredScopes: ['branches:create'],
  // Matches the public route: interim createBranch on the site's index page
  // until a site-scope create permission exists.
  permission: [{resource: 'SitePage', action: 'createBranch'}],
  confirmBeforeExecuting: true, // Forks the whole site; require user approval
  isConcurrencySafe: () => false,
  isReadOnly: () => false,
});

export type CreateSiteBranchArgumentType = z.infer<typeof argumentZod>;
